import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { PreFocusIndicator } from '../../styles/FocusIndicators';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { TabStyles } from '../../styles/TabStyles';
import { TextEllipsis } from '../../styles/TextEllipsis';

const Spacing = TabStyles.Spacing;
const Colors = TabStyles.Colors;
const FontColors = TabStyles.Typography.Color;

// Only the active tab should be visible, all others should be hidden
export const Visible: React.CSSProperties = { visibility: 'visible' };
export const Hidden: React.CSSProperties = { visibility: 'hidden' };

export const TabsWidgetDimensions = {
  tabLabelPadding: Spacing.ExtraLargeSpace,
  textOnlyTabListHeight: 42,
  iconAndTextTabListHeight: 54,
  tabIndicatorHeight: 3
};

// The client specifies which tab panel we should anchor the tab widget height to. It's often the tallest tab panel,
// so that the dialog will always be tall enough and will not resize when switching tabs
export const NonAnchorTabPanelStyles: React.CSSProperties = {
  position: 'absolute',
  width: '100%',
  left: '0',
  top: '0'
};

export const RootStyleName = TypeStyle.style({
  display: 'flex',
  flexDirection: 'column',
});

export const TabPanelScrollContainerStyles = (padding?: React.CSSProperties['padding']): React.CSSProperties => ({
  overflowY: 'auto',
  padding: padding ?? Spacing.LargeSpace,
  position: 'relative',
  WebkitOverflowScrolling: 'touch',
});

export const TabPanelContainerStyles: React.CSSProperties = {
  position: 'relative',
};

export const GetTabListStyles = (isAnIconDefinedInTabs: boolean, tabHeightOverride?: React.CSSProperties['height']): React.CSSProperties => {
  return {
    display: 'flex',
    flexShrink: 0,
    fontSize: isAnIconDefinedInTabs ? TabStyles.Typography.Sizing.Tiny : TabStyles.Typography.Sizing.Default,
    height: tabHeightOverride ?? (isAnIconDefinedInTabs ? TabsWidgetDimensions.iconAndTextTabListHeight : TabsWidgetDimensions.textOnlyTabListHeight),
    ...PreventUserSelect,
  };
};

export const TabStyleName = TypeStyle.style(PreFocusIndicator, {
  boxSizing: 'border-box',
  display: 'inline-block',
  height: '100%', // TFSID 773048
  overflow: 'hidden',
  position: 'relative',
  $nest: {
    '&::after': { // indicator
      backgroundColor: 'transparent',
      content: `""`,
      height: TabsWidgetDimensions.tabIndicatorHeight,
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
    },
    '&:hover::after': { backgroundColor: Colors.L5 },
    '&[aria-selected="true"]::after': { backgroundColor: Colors.L8 },
    '&&:active::after': { backgroundColor: Colors.L9 },
  }
});

export const GetTabLabelContainerStyles = (horizontalPadding?: React.CSSProperties['paddingLeft']): React.CSSProperties => {
  return {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    paddingRight: horizontalPadding ?? TabsWidgetDimensions.tabLabelPadding,
    paddingLeft: horizontalPadding ?? TabsWidgetDimensions.tabLabelPadding,
  };
};

export const DefaultTabLabelStyleName: string = TypeStyle.style({
  color: FontColors.PrimaryLight,
  width: '100%',
  ...TextEllipsis
});

export const TabContentStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center'
};

export const LabelStyle: React.CSSProperties = { display: 'block', ...TextEllipsis };
